import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "homeowner", liability_accepted: false });
  const [loading, setLoading] = useState(false);
  useSEO({ title: "Sign Up", description: "Join Kootenay Home Pros — connect homeowners with vetted Columbia Valley contractors. Subscriptions from $7.99/month." });

  const handle = async (e) => {
    e.preventDefault();
    if (form.role === "contractor" && !form.liability_accepted) {
      toast.error("You must accept the contractor liability terms");
      return;
    }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome ${u.name}!`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-5 py-20">
      <div className="bg-white border border-[#E2DCD0] rounded-xl p-8 shadow-sm">
        <h1 className="text-3xl font-bold mb-2 tracking-tight" style={{ fontFamily: "Outfit" }}>Join the valley</h1>
        <p className="text-sm text-stone-600 mb-8">Create your free account. Subscribe when you're ready.</p>

        <div className="grid grid-cols-2 gap-2 mb-5">
          {["homeowner", "contractor"].map(r => (
            <button
              key={r}
              type="button"
              data-testid={`signup-role-${r}`}
              onClick={() => setForm(f => ({...f, role: r}))}
              className={`py-3 rounded-lg border-2 text-sm font-semibold capitalize transition-all ${
                form.role === r ? "border-[#314A3E] bg-[#EAE5D9] text-[#314A3E]" : "border-[#E2DCD0] text-stone-600 hover:border-[#314A3E]/50"
              }`}
            >I'm a {r}</button>
          ))}
        </div>

        <form onSubmit={handle} className="space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input data-testid="signup-name-input" id="name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input data-testid="signup-email-input" id="email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          </div>
          <div>
            <Label htmlFor="pw">Password</Label>
            <Input data-testid="signup-password-input" id="pw" type="password" required minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
          </div>
          <Button data-testid="signup-submit-btn" type="submit" disabled={loading} className="w-full bg-[#314A3E] hover:bg-[#243a30] text-white">
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>

        {form.role === "contractor" && (
          <div className="mt-5 p-4 bg-[#FBF6EE] border-2 border-[#B85C47]/30 rounded-lg">
            <div className="overline text-[#B85C47] mb-2">Contractor Liability Agreement</div>
            <p className="text-xs text-stone-700 leading-relaxed mb-3">
              As a contractor on Kootenay Home Pros, you assume <span className="font-bold">all liability</span> for any work you perform.
              You agree to indemnify and hold harmless Kootenay Home Pros from all claims, damages, and disputes arising from your work,
              and to carry your own licenses, WorkSafeBC coverage, and general liability insurance.{" "}
              <Link to="/terms" className="text-[#B85C47] font-semibold underline">Read full terms</Link>.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="signup-liability-checkbox"
                type="checkbox"
                checked={form.liability_accepted}
                onChange={e => setForm({...form, liability_accepted: e.target.checked})}
                className="mt-0.5 w-4 h-4 accent-[#314A3E]"
              />
              <span className="text-xs text-stone-700">
                I have read and accept the Contractor Liability &amp; Indemnification Agreement.
              </span>
            </label>
          </div>
        )}

        <div className="my-6 flex items-center gap-3 text-xs text-stone-400">
          <div className="flex-1 h-px bg-[#E2DCD0]" /> OR <div className="flex-1 h-px bg-[#E2DCD0]" />
        </div>
        <Button data-testid="signup-google-btn" onClick={googleSignup} variant="outline" className="w-full border-[#E2DCD0]">
          Continue with Google
        </Button>

        <p className="text-sm text-center mt-6 text-stone-600">
          Have an account? <Link to="/login" className="text-[#B85C47] font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
